import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Radio, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { getNews } from "@/lib/api";

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 3600) return `il y a ${Math.max(1, Math.round(diff / 60))} min`;
  if (diff < 86400) return `il y a ${Math.round(diff / 3600)} h`;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
};

export const LiveNews = ({ country }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getNews(country)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData({ items: [] }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [country]);

  return (
    <div className="rounded-2xl border border-white/10 bg-navy/40 p-6" data-testid="live-news">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-70" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-gold" />
          </span>
          <h3 className="font-display text-lg text-white flex items-center gap-2"><Radio size={17} className="text-gold" /> Actualités en temps réel</h3>
        </div>
        <span className="text-xs text-white/40 font-body flex items-center gap-1">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          {data?.updated_at ? `MAJ ${timeAgo(data.updated_at)}` : ""}
        </span>
      </div>

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-gold" size={26} /></div>
      ) : (
        <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1" data-lenis-prevent data-testid="live-news-list">
          {(!data || data.items.length === 0) && <p className="text-white/50 text-sm font-body py-6">Actualités momentanément indisponibles.</p>}
          {data?.items?.map((it, i) => (
            <motion.a
              key={i} href={it.link} target="_blank" rel="noopener noreferrer"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.4) }}
              className="group flex items-start gap-3 rounded-xl border border-transparent hover:border-gold/30 hover:bg-white/5 p-3 transition-all"
              data-testid={`news-item-${i}`}
            >
              <span className="mt-0.5 shrink-0 rounded-full bg-gold/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-gold">{it.topic_label}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-body text-white/90 group-hover:text-white leading-snug">{it.title}</span>
                <span className="mt-1 flex items-center gap-2 text-[11px] text-white/45">{it.source} · {timeAgo(it.published)}</span>
              </span>
              <ExternalLink size={14} className="mt-1 shrink-0 text-white/30 group-hover:text-gold transition-colors" />
            </motion.a>
          ))}
        </div>
      )}
    </div>
  );
};
